import type { TraceLine } from '../api';

/**
 * Call-start waterfall: how the time between the call connecting and the caller
 * hearing the agent was actually spent.
 *
 * Built from `startup.mark` events as they arrive, so it fills in live while the
 * call is still connecting — which is when a stall is worth watching. The final
 * `startup.timeline` event carries the same marks and wins when present, since a
 * completed call may have been loaded from history with the individual marks
 * already trimmed out of the stream.
 *
 * Each gap is attributed to whoever owns it. That attribution is the whole point
 * of the panel: "agent was slow to speak" has at least four causes that look
 * identical from the outside — carrier ring time, our own backend round-trips,
 * the LiveKit plugin's realtime connect, and the model itself — and they have
 * nothing to do with each other.
 */

type Mark = { name: string; atMs: number; sincePrevMs: number };

/** Who to go looking at when a given phase is the one that ate the time. */
type Owner = 'carrier' | 'livekit' | 'ours' | 'model';

const OWNER_LABEL: Record<Owner, string> = {
  carrier: 'carrier / SIP',
  livekit: 'LiveKit',
  ours: 'our code',
  model: 'model',
};

/**
 * Ownership of the phase *ending* at each mark. Keyed by the mark that closes
 * the gap, because that is what the timeline records.
 */
const PHASE: Record<string, { owner: Owner; what: string }> = {
  entrypoint: { owner: 'livekit', what: 'job dispatch → entrypoint invoked' },
  room_connected: { owner: 'livekit', what: 'ctx.connect() — joining the room' },
  participant_joined: { owner: 'carrier', what: 'waiting for the SIP participant to join' },
  conversation_resolved: { owner: 'ours', what: 'inbound config lookup / conversation id' },
  agent_config_loaded: { owner: 'ours', what: 'fetchAgentConfig() round-trip' },
  customer_context_loaded: { owner: 'ours', what: 'fetchCustomerContext() round-trip' },
  realtime_model_created: { owner: 'ours', what: 'building the realtime model' },
  session_start_begin: { owner: 'ours', what: 'assembling the session' },
  session_start_done: { owner: 'livekit', what: 'session.start() — SDK session setup' },
  activity_ready: { owner: 'livekit', what: 'realtime socket connect (plugin)' },
  sip_active: { owner: 'carrier', what: 'ring time — waiting for the callee to answer' },
  recording_notice_done: { owner: 'ours', what: 'call-recording disclosure playback' },
  hello_audio_pushed: { owner: 'ours', what: 'pushing hello.wav as the greeting trigger' },
  hello_audio_failed: { owner: 'ours', what: 'hello.wav push failed' },
  greeting_sent: { owner: 'model', what: 'generateReply() for the greeting' },
  agent_speaking: { owner: 'model', what: 'model producing the first audio frame' },
};

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export function extractMarks(lines: TraceLine[]): Mark[] {
  // The summary event is authoritative — it is the complete, ordered set.
  for (let i = lines.length - 1; i >= 0; i--) {
    const raw = lines[i]!;
    if (raw.event !== 'startup.timeline') continue;
    const marks = raw.marks;
    if (!Array.isArray(marks)) continue;
    return marks.map((m) => {
      const rec = m as Record<string, unknown>;
      return {
        name: String(rec.name ?? '?'),
        atMs: num(rec.at_ms),
        sincePrevMs: num(rec.since_prev_ms),
      };
    });
  }

  // Otherwise accumulate the per-mark events — this is the live path.
  const seen = new Map<string, Mark>();
  for (const line of lines) {
    if (line.event !== 'startup.mark') continue;
    const name = String(line.mark ?? '');
    if (!name || seen.has(name)) continue; // first write wins, as in the agent
    seen.set(name, { name, atMs: num(line.at_ms), sincePrevMs: num(line.since_prev_ms) });
  }
  return [...seen.values()].sort((a, b) => a.atMs - b.atMs);
}

const secs = (ms: number): string => `${(ms / 1000).toFixed(2)}s`;

export function StartupWaterfall({ lines }: { lines: TraceLine[] }): JSX.Element | null {
  const marks = extractMarks(lines);
  if (!marks.length) return null;

  const total = marks[marks.length - 1]!.atMs;
  const firstAudio = marks.find((m) => m.name === 'agent_speaking')?.atMs ?? null;
  const span = Math.max(total, 1);

  // Which single phase cost the most. On most calls this is the answer, and
  // stating it saves reading the bars.
  const worst = marks.reduce((a, b) => (b.sincePrevMs > a.sincePrevMs ? b : a));
  const worstPhase = PHASE[worst.name];

  return (
    <div className="startup">
      <div className="legend">
        <strong>
          {firstAudio === null
            ? 'Call start → first audio: never spoke'
            : `Call start → first audio: ${secs(firstAudio)}`}
        </strong>
        <span style={{ marginLeft: 'auto' }}>
          {worstPhase
            ? `slowest phase: ${worst.name} ${secs(worst.sincePrevMs)} · ${OWNER_LABEL[worstPhase.owner]}`
            : `slowest phase: ${worst.name} ${secs(worst.sincePrevMs)}`}
        </span>
      </div>

      {marks.map((m) => {
        const phase = PHASE[m.name];
        const start = m.atMs - m.sincePrevMs;
        return (
          <div className="startup__row" key={m.name}>
            <span className="startup__name" title={phase?.what}>
              {m.name}
            </span>
            <div
              className="turn__bar"
              title={
                `${phase?.what ?? m.name}\n` +
                `took ${secs(m.sincePrevMs)}, done at +${secs(m.atMs)}` +
                (phase ? `\nowner: ${OWNER_LABEL[phase.owner]}` : '')
              }
            >
              {/* Offset spacer, then the phase's own duration — so each row
                  sits where it actually happened on the call's clock. */}
              <span className="turn__seg" style={{ width: `${(start / span) * 100}%` }} />
              <span
                className={`turn__seg startup__seg--${phase?.owner ?? 'ours'}`}
                style={{ width: `${Math.max((m.sincePrevMs / span) * 100, 0.6)}%` }}
              />
            </div>
            <span className="turn__total">{secs(m.sincePrevMs)}</span>
            <span className="startup__owner">{phase ? OWNER_LABEL[phase.owner] : ''}</span>
          </div>
        );
      })}
    </div>
  );
}
